import {useEffect, useState} from "react";
import * as XLSX from 'xlsx';

export default function QuotationsViewByElements({state}) {

    const [selectedRow, setSelectedRow] = useState(null)
    const [quotationList, setQuotationList] = useState([])
    const getBeautifulAmount = (value) => {
        let val = value
        val = new Intl.NumberFormat('fr-FR')?.format(val)
        return val
    }

    useEffect(() => {
        setQuotationList(getGroupedProducts(state.quotationList))
    }, [state.quotationList]);

    const getGroupedProducts = (quotationArray) => {
        // Créer un dictionnaire pour stocker les produits regroupés
        const groupedProducts = {};

        // Vérifier que quotationArray est un tableau
        if (!Array.isArray(quotationArray)) {
            console.error("quotationArray n'est pas un tableau");
            return [];
        }

        // Parcourir chaque quotation dans le tableau
        quotationArray.forEach(quotation => {
            // Stocker le typeId et typeName pour chaque produit
            const typeId = quotation.typeId;
            const typeName = quotation.typeName;

            // Parcourir tous les sites
            quotation?.sites?.forEach(site => {
                // Parcourir tous les devis du site
                site.devis.forEach(devis => {
                    // Parcourir toutes les interventions du devis
                    devis.interventions.forEach(intervention => {
                        // Parcourir tous les devis de l'intervention
                        intervention.quotations.forEach(innerQuotation => {
                            // Parcourir tous les produits du devis
                            innerQuotation.products.forEach(product => {
                                // Créer une clé unique basée sur les 5 critères (ajout du typeId)
                                const key = `${typeId}_${site.id}_${product.productName}_${product.qty}_${product.pvht}`;

                                // Si cette clé existe déjà, incrémenter le compteur
                                if (groupedProducts[key]) {
                                    groupedProducts[key].count += 1;
                                    // Ajouter la référence et l'ID du devis
                                    groupedProducts[key].quotations.push({
                                        ref: innerQuotation.odooRef,
                                        id: innerQuotation.id
                                    });
                                } else {
                                    // Sinon, créer une nouvelle entrée
                                    groupedProducts[key] = {
                                        typeId: typeId,
                                        typeName: typeName,
                                        siteId: site.id,
                                        siteName: site.name,
                                        siteVille: site.ville,
                                        siteCP: site.cp,
                                        productName: product.productName,
                                        pvht: product.pvht,
                                        qty: product.qty,
                                        unit: product.unit,
                                        count: 1,
                                        // Stocker à la fois la référence et l'ID du devis
                                        quotations: [{
                                            ref: innerQuotation.odooRef,
                                            id: innerQuotation.id
                                        }]
                                    };
                                }
                            });
                        });
                    });
                });
            });
        });

        // Convertir le dictionnaire en tableau
        return Object.values(groupedProducts);
    };

    const exportToExcel = () => {
        // Create workbook
        const wb = XLSX.utils.book_new();

        // Group data by element type
        const elementTypes = [...new Set(state?.quotationList?.map(quotation => quotation.typeId))];

        // Process each element type
        elementTypes.forEach(typeId => {
            const currentType = state.quotationList.find(q => q.typeId === typeId);
            const typeName = currentType.typeName;

            // Prepare data for this element type
            const sheetData = [];

            // Add headers
            sheetData.push([
                "Site", // Colonne combinée pour site, CP et ville
                "Produit",
                "Prix unitaire HT(€)",
                "Quantité",
                "Unité",
                "Total HT (€)",
                "Références"
            ]);

            // Process sites for this element type
            currentType.sites.forEach(site => {
                // Filter quotationList for this type and site
                const siteProducts = quotationList
                    .filter(group => group.typeId === typeId && group.siteId === site.id);

                // Créer une chaîne formatée pour le site
                const siteInfo = `${site.name}, ${site.cp} ${site.ville}`;

                // Garder trace des lignes pour la fusion des cellules
                const startRow = sheetData.length;

                siteProducts.forEach((group, index) => {
                    // Format references
                    let references = "";
                    if (group.count > 1) {
                        references = group.quotations.map(q => q.ref).join(", ");
                    } else {
                        references = group.quotations[0].ref;
                    }

                    // Add row to sheet data
                    sheetData.push([
                        siteInfo, // Information de site combinée
                        group.productName,
                        group.pvht,
                        group.qty,
                        group.unit,
                        group.pvht * group.qty,
                        references
                    ]);
                });

                // Enregistrer la plage de fusion si nous avons plus d'une ligne pour ce site
                const endRow = sheetData.length - 1;
                if (endRow > startRow) {
                    // Nous stockerons les plages de fusion pour les appliquer après la création de la feuille
                    if (!wb.mergeCells) {
                        wb.mergeCells = [];
                    }

                    // Stocker la plage de fusion pour cette colonne de site (colonne A)
                    wb.mergeCells.push({
                        s: { r: startRow, c: 0 },
                        e: { r: endRow, c: 0 }
                    });
                }
            });

            const totalAmount = sheetData.slice(1).reduce((sum, row) => sum + (row[5] || 0), 0);

            sheetData.push([]);
            sheetData.push([]);

            sheetData.push([
                "TOTAL",
                "",
                "",
                "",
                "",
                "",
                totalAmount
            ]);


            // Create worksheet for this element type
            const ws = XLSX.utils.aoa_to_sheet(sheetData);

            // Appliquer les fusions de cellules si elles existent
            if (wb.mergeCells && wb.mergeCells.length > 0) {
                ws['!merges'] = wb.mergeCells;
                // Réinitialiser pour la prochaine feuille
                wb.mergeCells = [];
            }

            // Format currency cells
            const priceColumns = [2, 5]; // Colonnes avec prix (0-indexed) - ajustées après la fusion des colonnes
            const range = XLSX.utils.decode_range(ws['!ref']);


            for (let row = 1; row <= range.e.r; row++) { // Start from 1 to skip header
                for (let col of priceColumns) {
                    const cellRef = XLSX.utils.encode_cell({r: row, c: col});
                    if (ws[cellRef]) {
                        ws[cellRef].z = '#,##0.00 €';
                    }
                }
            }

            // Add worksheet to workbook
            // Sanitize sheet name to avoid invalid characters
            const safeSheetName = typeName.replace(/[\[\]\*\/\\\?:]/g, '_').substring(0, 31);
            XLSX.utils.book_append_sheet(wb, ws, safeSheetName);
        });

        // Generate Excel file and trigger download
        XLSX.writeFile(wb, "Vue_par_elements.xls", { bookType: 'xls' });
    };

    return (

        <div style={{display: "grid", width: "94vw", rowGap: ".5rem"}}>

            <div style={{
                width: "100%",
                padding: ".5rem"
            }}>
            </div>

            <button
                onClick={exportToExcel}
                style={{
                    padding: "0.5rem 1rem",
                    backgroundColor: "#4CAF50",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer"
                }}
            >
                Exporter en Excel
            </button>
            {
                state?.quotationList?.map((quotation, index) =>
                        <div key={index} className={"site-quotation-row parent"} onClick={() => {
                            setSelectedRow(selectedRow == quotation.typeId ? null : quotation.typeId)
                        }
                        }>
                            <div style={{display: "flex", flexDirection: "row", justifyContent: "space-between"}}>
                                <h2>
                                    {quotation.typeName}
                                    {" "}
                                    <span style={{fontSize: "0.8em", color: "#666"}}>
            ({
                                        // Compter les sites uniques pour ce type d'élément
                                        new Set(
                                            quotationList
                                                .filter(group => group.typeId === quotation.typeId)
                                                .map(group => group.siteId)
                                        ).size
                                    } sites)
        </span>
                                </h2>
                                <h2>
                                    {
                                        getBeautifulAmount(
                                            quotationList
                                                .filter(group => group.typeId === quotation.typeId)
                                                .reduce((acc, group) => acc + (group.pvht * group.qty), 0)
                                        )
                                    }€
                                </h2>
                            </div>
                            {
                                selectedRow == quotation.typeId &&
                                <div style={{display: "flex", flexDirection: "column", gap: '1rem'}}>
                                    {/*{
                                    quotation.sites.map((site, index) =>

                                        <div key={index}>
                                            <div>
                                                <b>{site.name}</b><br /> {site.cp}, {site.ville}
                                            </div>
                                                {
                                                    site.devis.map((devis, index) =>
                                                        <div key={index}>
                                                            &nbsp;&nbsp;&nbsp;&nbsp;<a onClick={(e) => e.stopPropagation()} target={"_blank"} href={`/devis/${devis.id}`}>{devis.clientIdBCommande}</a>
                                                            {
                                                                devis.interventions.map((intervention, index) =>
                                                                    <div key={index}>
                                                                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<a onClick={(e) => e.stopPropagation()} target={"_blank"} href={`/Intervention/${intervention.id}`}>Intervention #{intervention.id}</a>
                                                                        {
                                                                            intervention.quotations.map((quotation, index) =>
                                                                                <div key={index} style={{padding: "0 4rem"}}>
                                                                                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<a onClick={(e) => e.stopPropagation()} target={"_blank"} href={`/quotation/${quotation.id}`}>{quotation.odooRef}</a>

                                                                                    <div key={index} style={{
                                                                                        display: "inline-grid",
                                                                                        gridTemplateColumns: "repeat(4, 1fr)",
                                                                                        width: "100%",
                                                                                        justifyItems: "center",
                                                                                        alignItems: "center",
                                                                                        rowGap: ".5rem"
                                                                                    }}>
                                                                                        {
                                                                                        quotation.products.map((product, index) =>
                                                                                            <>
                                                                                                <div><b>{product.productName}</b></div>
                                                                                                <div>{getBeautifulAmount(product.pvht)}€</div>
                                                                                                <div>{product.qty}{product.unit}</div>
                                                                                                <div>{getBeautifulAmount(product.pvht * product.qty)}€</div>
                                                                                            </>
                                                                                        )
                                                                                    }
                                                                                </div>
                                                                                </div>
                                                                            )
                                                                        }
                                                                    </div>
                                                                )
                                                            }

                                                        </div>
                                                    )
                                                }

                                        </div>
                                    )
                                }*/}

                                    {
                                        quotation.sites.map((site, index) =>
                                                <div key={index}>
                                                    <div>
                                                        <a
                                                            onClick={(e) => e.stopPropagation()}
                                                            target={"_blank"}
                                                            href={`/site/${site.id}`}>
                                                            <b>{site.name}</b>
                                                        </a>
                                                        <br/> {site.cp}, {site.ville}
                                                    </div>

                                                    {/* Utiliser les produits regroupés pour ce site */}
                                                    {
                                                        quotationList
                                                            .filter(group => group.typeId === quotation.typeId && group.siteId === site.id)
                                                            .map((group, groupIndex) => (
                                                                <div key={groupIndex} style={{
                                                                    display: "grid",
                                                                    gridTemplateColumns: "repeat(5, 1fr)",
                                                                    width: "100%",
                                                                    justifyItems: "start",
                                                                    alignItems: "center",
                                                                    padding: "0.5rem 2rem",
                                                                    borderBottom: "1px solid #eee"
                                                                }}>
                                                                    <div><b>{group.productName}</b></div>
                                                                    <div>{getBeautifulAmount(group.pvht)}€</div>
                                                                    <div>{group.qty}{group.unit}</div>
                                                                    <div>{getBeautifulAmount(group.pvht * group.qty)}€</div>
                                                                    <div>
                                                                        {group.count > 1 ?
                                                                            <>
                                                                                {group.count} occurrences dans {' '}
                                                                                {group.quotations.map((q, i) => (
                                                                                    <span key={i}>
                    {i > 0 && ", "}
                                                                                        <a
                                                                                            onClick={(e) => e.stopPropagation()}
                                                                                            target={"_blank"}
                                                                                            href={`/quotation/${q.id}`}
                                                                                        >
                        {q.ref}
                    </a>
                </span>
                                                                                ))}
                                                                            </> :
                                                                            <>
                                                                                Dans {' '}
                                                                                <a
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                    target={"_blank"}
                                                                                    href={`/quotation/${group.quotations[0].id}`}
                                                                                >
                                                                                    {group.quotations[0].ref}
                                                                                </a>
                                                                            </>
                                                                        }
                                                                    </div>
                                                                </div>
                                                            ))
                                                    }
                                                </div>
                                        )
                                    }
                                </div>
                            }
                        </div>
                )
            }
        </div>
    )
}